import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/authStore";
import { useBrandOnboarding } from "@/context/BrandOnboardingContextFixed";
import logo from '../../assets/images/envelope-artie.png';

export default function BrandOnboardingStep4Fixed() {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login, checkAuth } = useAuthStore();
  const { refreshStatus, status, isLoading: isStatusLoading, setIsCompletingOnboarding } = useBrandOnboarding();
  
  // Flag to prevent navigation logic when user explicitly clicks "Go to Dashboard"
  const isCompletingOnboardingRef = useRef(false);

  useEffect(() => {
    // Skip navigation logic if user is completing onboarding
    if (isCompletingOnboardingRef.current) {
      console.log("⏸️ BrandOnboardingStep4: Skipping navigation logic - user completing onboarding");
      return;
    }
    
    console.log("🔍 BrandOnboardingStep4: useEffect triggered", {
      timestamp: new Date().toISOString(),
      isStatusLoading,
      status: status?.onboarding_status,
      hasStatus: !!status
    });
    
    const currentPath = window.location.pathname;
    
    if (!isStatusLoading && status && status.onboarding_status) {
      console.log("🧭 BrandOnboardingStep4: Checking navigation conditions", {
        onboardingStatus: status.onboarding_status,
        currentPath
      });
      
      if (status.onboarding_status === "account_created" && currentPath === "/brand-onboarding/complete") {
        console.log("🧭 BrandOnboardingStep4: Navigating to company-info");
        setTimeout(() => {
          navigate("/brand-onboarding/company-info", { replace: true });
        }, 100);
      } else if (status.onboarding_status === "company_info_added" && currentPath === "/brand-onboarding/complete") {
        console.log("🧭 BrandOnboardingStep4: Navigating to brand-profile");
        setTimeout(() => {
          navigate("/brand-onboarding/brand-profile", { replace: true });
        }, 100);
      } else if (status.onboarding_status === "brand_profile_completed") {
        // User has completed brand profile and is now on completion page
        // This is correct - they may have just saved preferences and navigated here
        console.log("✅ BrandOnboardingStep4: Brand profile completed - staying on completion page");
      } else if (status.onboarding_status === "onboarding_complete") {
        // Only auto-navigate to dashboard if user didn't explicitly click the button
        console.log("✅ BrandOnboardingStep4: Onboarding complete - staying on completion page");
      }
    }
  }, [status?.onboarding_status, isStatusLoading, navigate]);

  async function onComplete() {
    setIsLoading(true);
    isCompletingOnboardingRef.current = true; // Set local flag to prevent navigation logic
    setIsCompletingOnboarding(true); // Set global flag to prevent all navigation
    
    console.log("🚀 BrandOnboardingStep4: Completing onboarding", {
      timestamp: new Date().toISOString()
    });
    
    try {
      console.log("🌐 BrandOnboardingStep4: Making API call to completeBrandOnboarding");
      await apiClient.completeBrandOnboarding({
        ai_processing_consent: true,
        data_sharing_consent: true,
      });
      console.log("✅ BrandOnboardingStep4: completeBrandOnboarding successful");

      // Update local user state
      console.log("🔄 BrandOnboardingStep4: Updating login state");
      login();
      
      // Force update auth store to get new onboarding_completed status
      console.log("🔄 BrandOnboardingStep4: Refreshing auth state");
      await checkAuth();
      
      // Don't refresh status here to avoid triggering navigation logic
      // The dashboard will handle any necessary auth checks
      
      toast.success("Welcome to Artie! Your brand account is ready.");
      
      // Direct navigation to dashboard after successful completion
      console.log("🧭 BrandOnboardingStep4: Navigating directly to brand-dashboard");
      setTimeout(() => {
        navigate("/brand-dashboard", { replace: true });
      }, 1000); // Give time for toast to show
      
    } catch (error) {
      console.error("❌ BrandOnboardingStep4: Onboarding completion failed", error);
      toast.error(error instanceof Error ? error.message : "Failed to complete onboarding");
      isCompletingOnboardingRef.current = false; // Reset local flag on error
      setIsCompletingOnboarding(false); // Reset global flag on error
    } finally {
      setIsLoading(false);
      console.log("🏁 BrandOnboardingStep4: Onboarding completion process finished");
    }
  }

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">

      <main className="relative z-10 w-full flex flex-col items-center min-h-screen">

        <div className="w-full max-w-3xl px-6 py-12 flex flex-col items-center flex-grow">
          {/* Logo */}
          <div className="mb-8 relative">
            <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
            <img
              src={logo}
              alt="Artie"
              className="w-48 h-48 object-contain relative z-10"
            />
          </div>

          {/* Header Section */}
          <div className="text-center max-w-2xl mb-10 animate-in slide-in-from-bottom-4 duration-500">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
              You're all set!
            </h1>
            <p className="text-lg text-[oklch(0.55_0_0)] font-ibm leading-relaxed mb-2">
              Your profile is complete and preferences are set.
            </p>
          </div>

          {/* Success Card */}
          <div className="w-full max-w-md mb-8 p-8 bg-white shadow-xl border border-[oklch(0.92_0_0)] rounded-2xl flex flex-col items-center text-center animate-in slide-in-from-bottom-4 duration-500 delay-100">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-[oklch(0.2686_0_0)] mb-2 font-cabinet">
              Ready to match
            </h3>
            <p className="text-[oklch(0.55_0_0)] font-ibm leading-relaxed">
              We're ready to start finding the perfect creators for your brand. Click below to enter your dashboard.
            </p>
          </div>

        </div>

        {/* Footer & Navigation */}
        <div className="w-full mt-auto pb-10 px-6 md:px-20">
          <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
          <div className="w-full flex items-center justify-between">
            <div className="flex-1 flex justify-start">
              <Button
                variant="ghost"
                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                onClick={() => navigate("/brand-onboarding/brand-preferences")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>

            {/* Progress Indicators */}
            <div className="flex gap-2 justify-center">
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.2686_0_0)]"></div>
            </div>

            <div className="flex-1 flex justify-end">
              <Button
                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onComplete}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    Finishing... <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                  </>
                ) : (
                  <>
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}